import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import type { ScratchVm } from "./scratch-editor";
import { csrfHeaders, type AuthenticatedSession } from "./session";
import { translate as t } from "./locale";
import { composePlanSvg } from "./plan-svg";
import { aiRequestKey, clearAiSuccess, rememberAiSuccess, wasAiRequestSuccessful } from "./ai-success";
import { trackTelemetryAction } from "./telemetry";

type Mode = "code" | "plan";
interface Operation { action: "add" | "replace" | "delete"; scriptId?: string; xml?: string }
interface Quota { codeRemaining: number; planRemaining: number; resetAt: string }
interface Snapshot { targetId: string; targetName: string; projectContext: object }
interface GameSnapshot { projectContext: object }
interface PlanAsset { kind: "costume" | "backdrop"; name: string; description: string }
interface PlanVariable { id: string; name: string; type: "" | "list" }
interface PlanBroadcast { id: string; name: string; type: "broadcast_msg" }
interface PlanTarget {
    targetId: string | null;
    key: string;
    name: string;
    isStage: boolean;
    assets: PlanAsset[];
    variables: PlanVariable[];
    scripts: { scriptId: string | null; xml: string | null }[];
}
interface GamePlan { summary: string; targets: PlanTarget[]; broadcasts: PlanBroadcast[]; quota: Quota }

interface AssistantBridge {
    captureAssistantProject: (vm: ScratchVm) => Snapshot;
    captureAssistantGameProject: (vm: ScratchVm) => GameSnapshot;
    validateAssistantGamePlan: (snapshot: GameSnapshot, plan: GamePlan) => void;
    renderAssistantGamePlanTarget: (snapshot: GameSnapshot, plan: GamePlan, key: string) => Promise<string[]>;
    applyAssistantCode: (vm: ScratchVm, snapshot: Snapshot, operations: Operation[]) => void;
}

const bridge = (): AssistantBridge => window.GUI;

const errorText = (reason: string): string | undefined => {
    switch (reason) {
        case "SELECT_SPRITE": return t("Wybierz duszka w edytorze.");
        case "PROJECT_TOO_LARGE": return t("Kod projektu jest zbyt duży dla asystenta.");
        case "SCRIPT_CHANGED": return t("Wybrany duszek zmienił się. Wyślij prompt ponownie.");
        case "INVALID_BLOCKS": return t("Asystent zwrócił niepoprawne bloczki. Projekt nie został zmieniony.");
        case "EDITOR_UNAVAILABLE": return t("Edytor bloczków nie jest gotowy. Poczekaj na wczytanie projektu i spróbuj ponownie.");
        case "AI_INVALID_PROJECT": return t("Nie udało się odczytać poprawnego stanu projektu. Wczytaj projekt ponownie.");
        case "AI_TARGET_MISMATCH": return t("Wybrany duszek nie zgadza się ze stanem projektu. Wybierz go ponownie.");
        case "AI_UNAVAILABLE": return t("Asystent nie jest skonfigurowany na serwerze.");
        case "AI_TIMEOUT": return t("Asystent nie odpowiedział w wyznaczonym czasie. Spróbuj ponownie.");
        case "AI_PROVIDER_ERROR": return t("Usługa asystenta jest chwilowo niedostępna. Spróbuj ponownie.");
        case "AI_MODERATION_BLOCKED": return t("Ta prośba nie może zostać zrealizowana. Zmień opis i spróbuj ponownie.");
        case "AI_MODERATION_UNAVAILABLE": return t("Sprawdzenie prośby jest chwilowo niedostępne. Spróbuj ponownie później.");
        default: return undefined;
    }
};

const invalidResponseText = (reason?: string): string => {
    switch (reason) {
        case "variable-reference": return t("Asystent podał nieprawidłowe odwołanie do zmiennej lub listy. Spróbuj ponownie.");
        case "broadcast-reference": return t("Asystent podał niepoprawne odwołanie do komunikatu. Spróbuj ponownie.");
        case "script-xml-malformed": return t("Asystent zwrócił uszkodzony zapis bloczków. Spróbuj ponownie.");
        case "script-xml-opcode": return t("Asystent użył bloczka niedostępnego w Scratch. Spróbuj ponownie.");
        case "script-xml-number": return t("Asystent podał niepoprawną liczbę w bloczku. Spróbuj ponownie.");
        case "script-xml-key": return t("Asystent podał niepoprawną wartość klawisza. Spróbuj ponownie.");
        case "script-xml-missing-input":
        case "script-xml-empty-input": return t("Asystent pominął wymagany parametr bloczka. Spróbuj ponownie.");
        case "code-coverage": return t("Asystent nie uwzględnił wszystkich opisanych działań. Spróbuj ponownie.");
        case "sprite-count": return t("W planie brakuje żądanej liczby duszków. Spróbuj ponownie.");
        default: return t("Asystent zwrócił niepoprawne bloczki. Projekt nie został zmieniony.");
    }
};

export const AiAssistant = ({
    onWorkingChange,
    projectId,
    session,
    vm,
}: {
    onWorkingChange?: (working: boolean) => void;
    projectId?: string;
    session?: AuthenticatedSession;
    vm?: ScratchVm;
}) => {
    const [mode, setMode] = useState<Mode>("code");
    const [prompt, setPrompt] = useState("");
    const [quota, setQuota] = useState<Quota>();
    const [working, setWorking] = useState(false);
    const [stoppable, setStoppable] = useState(false);
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const [status, setStatus] = useState("");
    const [plan, setPlan] = useState<GamePlan>();
    const [planImage, setPlanImage] = useState("");
    const [planLink, setPlanLink] = useState("");
    const [planOpen, setPlanOpen] = useState(false);
    const [targetName, setTargetName] = useState("");
    const closePlanButton = useRef<HTMLButtonElement>(null);
    const planUrl = useRef("");
    const scope = useRef({ projectId, userId: session?.user.id, vm });
    const activeProject = useRef(projectId);
    const activeVm = useRef(vm);
    const activeUser = useRef(session?.user.id);
    const requestStartedAt = useRef(0);
    const requestVersion = useRef(0);
    const requestController = useRef<AbortController>();
    const previousCodeRequest = useRef<{projectId?: string; userId: string; targetId: string; prompt: string}>();
    useEffect(() => { onWorkingChange?.(working); }, [onWorkingChange, working]);
    useEffect(() => {
        if (!working) return;
        const interval = window.setInterval(() => {
            setElapsedSeconds(Math.floor((Date.now() - requestStartedAt.current) / 1000));
        }, 1000);
        return () => window.clearInterval(interval);
    }, [working]);
    useEffect(() => { activeProject.current = projectId; }, [projectId]);
    useEffect(() => { activeVm.current = vm; }, [vm]);
    useEffect(() => { activeUser.current = session?.user.id; }, [session]);
    useEffect(() => {
        if (scope.current.projectId === projectId && scope.current.userId === session?.user.id && scope.current.vm === vm)
            return;
        requestVersion.current++;
        requestController.current?.abort();
        requestController.current = undefined;
        setStoppable(false);
        previousCodeRequest.current = undefined;
        setWorking(false);
        if (scope.current.vm) clearAiSuccess(scope.current.vm);
        if (vm) clearAiSuccess(vm);
        scope.current = { projectId, userId: session?.user.id, vm };
        if (planUrl.current) URL.revokeObjectURL(planUrl.current);
        planUrl.current = "";
        setPlan(undefined);
        setPlanImage("");
        setPlanLink("");
        setPlanOpen(false);
        setStatus("");
        setPrompt("");
    }, [projectId, session?.user.id, vm]);
    useEffect(() => () => {
        requestVersion.current++;
        requestController.current?.abort();
        if (planUrl.current) URL.revokeObjectURL(planUrl.current);
    }, []);
    useEffect(() => {
        if (!planOpen) return;
        closePlanButton.current?.focus();
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") setPlanOpen(false);
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [planOpen]);
    useEffect(() => {
        if (!session) return;
        let active = true;
        void fetch("/api/ai/quota", { credentials: "same-origin" })
            .then(async response => {
                if (response.ok && active) setQuota(await response.json() as Quota);
            })
            .catch(() => {});
        return () => { active = false; };
    }, [session]);

    useEffect(() => {
        if (!vm) return;
        const update = () => {
            try { setTargetName(bridge().captureAssistantProject(vm).targetName); }
            catch { setTargetName(""); }
        };
        update();
        // The editor emits this event when the selected target changes.
        const eventVm = vm as ScratchVm & {
            on: (event: string, callback: () => void) => void;
            removeListener: (event: string, callback: () => void) => void;
        };
        eventVm.on("targetsUpdate", update);
        return () => eventVm.removeListener("targetsUpdate", update);
    }, [vm]);

    const stop = () => {
        if (!stoppable) return;
        requestVersion.current++;
        requestController.current?.abort();
        requestController.current = undefined;
        setStoppable(false);
        setWorking(false);
        setElapsedSeconds(0);
        setStatus(t("Zatrzymano pracę asystenta."));
    };

    const submit = async () => {
        if (!session || !vm || !prompt.trim() || working) return;
        const version = ++requestVersion.current;
        const controller = new AbortController();
        requestController.current = controller;
        setStoppable(true);
        let phase: "project" | "request" | "response" | "editor" | "preview" = "project";
        let attemptId: string | undefined;
        requestStartedAt.current = Date.now();
        setElapsedSeconds(0);
        setWorking(true);
        setStatus("");
        setPlanOpen(false);
        try {
            const snapshot = mode === "code" ? bridge().captureAssistantProject(vm) :
                bridge().captureAssistantGameProject(vm);
            const requestKey = aiRequestKey(session.user.id, projectId, mode, prompt,
                mode === "code" ? (snapshot as Snapshot).targetId : undefined);
            if (wasAiRequestSuccessful(vm, requestKey, snapshot.projectContext)) {
                trackTelemetryAction("ai_result", { mode, result: "rejected", reason: "duplicate" });
                setStatus(mode === "plan" ?
                    t("Generacja dla tego opisu została już wykonana. Aby ponownie wyświetlić plan, kliknij „Pokaż propozycję bloczków”.") :
                    t("Generacja dla tego opisu została już wykonana."));
                return;
            }
            const startedProject = projectId;
            const previous = previousCodeRequest.current;
            const body = JSON.stringify({
                mode, prompt: prompt.trim(),
                ...(mode === "code" ? { targetId: (snapshot as Snapshot).targetId } : {}),
                ...(mode === "code" && previous && previous.projectId === projectId &&
                    previous.userId === session.user.id && previous.targetId === (snapshot as Snapshot).targetId &&
                    previous.prompt !== prompt.trim() ? {priorRequest: previous.prompt} : {}),
                projectContext: snapshot.projectContext,
            });
            if (new Blob([body]).size > 256 * 1024) throw new Error("PROJECT_TOO_LARGE");
            phase = "request";
            const response = await fetch("/api/ai/assist", {
                method: "POST",
                credentials: "same-origin",
                headers: { "Content-Type": "application/json", ...csrfHeaders(session) },
                body,
                signal: controller.signal,
            });
            if (version !== requestVersion.current) return;
            attemptId = response.headers.get("X-Scratch-CG-AI-Attempt-ID") ?? undefined;
            if (!response.ok) {
                const failure = await response.json().catch(() => ({})) as { error?: string; reason?: string; quota?: Quota };
                if (version !== requestVersion.current) return;
                trackTelemetryAction("ai_result", {
                    mode,
                    result: [400, 401, 403, 429].includes(response.status) ? "rejected" : "failure",
                    reason: "response",
                    httpStatus: String(response.status),
                    ...(attemptId ? { attemptId } : {}),
                });
                if (failure.quota) setQuota(failure.quota);
                if (response.status === 429) setStatus(t("Dzienny limit tego trybu został wykorzystany."));
                else if (failure.error === "AI_MODERATION_BLOCKED" || failure.error === "AI_MODERATION_UNAVAILABLE")
                    setStatus(errorText(failure.error) ?? "");
                else if (response.status === 401 || response.status === 403) setStatus(t("Zaloguj się ponownie, aby użyć asystenta."));
                else if (failure.error === "AI_INVALID_RESPONSE") setStatus(invalidResponseText(failure.reason));
                else {
                    const fallback = response.status === 504 ? "AI_TIMEOUT" :
                        [502, 503].includes(response.status) ? "AI_PROVIDER_ERROR" : "";
                    setStatus(errorText(failure.error ?? fallback) ?? (response.status === 422 ?
                        invalidResponseText() :
                        t("Serwer odrzucił żądanie asystenta (HTTP {status}).", { status: String(response.status) })));
                }
                return;
            }
            phase = "response";
            const result = await response.json() as GamePlan & { operations: Operation[] };
            if (version !== requestVersion.current) return;
            setQuota(result.quota);
            if (activeProject.current !== startedProject || activeVm.current !== vm ||
                activeUser.current !== session.user.id ||
                (mode === "code" && vm.editingTarget?.id !== (snapshot as Snapshot).targetId))
                throw new Error("SCRIPT_CHANGED");
            if (mode === "code") {
                phase = "editor";
                setStoppable(false);
                bridge().applyAssistantCode(vm, snapshot as Snapshot, result.operations);
                // Blockly delivers grouped block events after the apply call; capture the settled VM state.
                await new Promise<void>(resolve => window.setTimeout(resolve, 150));
                if (version !== requestVersion.current) return;
                if (activeProject.current !== startedProject || activeVm.current !== vm ||
                    activeUser.current !== session.user.id) throw new Error("SCRIPT_CHANGED");
                if (vm.editingTarget?.id === (snapshot as Snapshot).targetId)
                    rememberAiSuccess(vm, requestKey, bridge().captureAssistantProject(vm).projectContext);
                // eslint-disable-next-line require-atomic-updates -- request versions exclude stale async responses.
                previousCodeRequest.current = {
                    projectId, userId: session.user.id, targetId: (snapshot as Snapshot).targetId, prompt: prompt.trim(),
                };
            }
            else {
                phase = "preview";
                bridge().validateAssistantGamePlan(snapshot, result);
                const sections = await Promise.all(result.targets.map(async target => ({
                    name: target.name,
                    isStage: target.isStage,
                    images: await bridge().renderAssistantGamePlanTarget(snapshot, result, target.key),
                })));
                if (version !== requestVersion.current) return;
                if (activeProject.current !== startedProject || activeVm.current !== vm ||
                    activeUser.current !== session.user.id)
                    throw new Error("SCRIPT_CHANGED");
                const image = composePlanSvg(sections);
                const blob = new Blob([decodeURIComponent(image.slice(image.indexOf(",") + 1))], { type: "image/svg+xml" });
                const link = URL.createObjectURL(blob);
                if (planUrl.current) URL.revokeObjectURL(planUrl.current);
                planUrl.current = link;
                setPlanImage(image);
                setPlanLink(link);
                setPlan(result);
                setPlanOpen(true);
                rememberAiSuccess(vm, requestKey, snapshot.projectContext);
            }
            trackTelemetryAction("ai_result", { mode, result: "success", ...(attemptId ? { attemptId } : {}) });
        } catch (error) {
            if (version !== requestVersion.current) return;
            trackTelemetryAction("ai_result", {
                mode,
                result: "failure",
                reason: error instanceof Error && error.message === "SCRIPT_CHANGED" ? "project" : phase,
                ...(attemptId ? { attemptId } : {}),
            });
            const known = errorText(error instanceof Error ? error.message : "");
            setStatus(known ?? (phase === "project" ?
                t("Nie udało się odczytać projektu w edytorze. Poczekaj na jego wczytanie i spróbuj ponownie.") :
                phase === "request" ? t("Nie udało się połączyć z asystentem. Sprawdź połączenie i spróbuj ponownie.") :
                    phase === "response" ? t("Serwer zwrócił odpowiedź, której nie można odczytać.") :
                        phase === "editor" ? t("Odpowiedź dotarła, ale edytor nie mógł zastosować bloczków.") :
                            t("Odpowiedź dotarła, ale nie udało się wyświetlić propozycji bloczków.")));
        } finally {
            if (version === requestVersion.current) {
                requestController.current = undefined;
                setStoppable(false);
                setWorking(false);
                setElapsedSeconds(0);
            }
        }
    };

    return (
        <section className="conversation ai-assistant" aria-labelledby="ai-assistant-title">
            <h2 id="ai-assistant-title">{t("Asystent AI")}</h2>
            {!session ? <p>{t("Zaloguj się, aby korzystać z asystenta AI.")}</p> : <>
                <div className="ai-modes" role="group" aria-label={t("Tryb asystenta")}>
                    <button aria-pressed={mode === "code"} disabled={working} onClick={() => { setMode("code"); setPlanOpen(false); setStatus(""); }} type="button">{t("koduj")}</button>
                    <button aria-pressed={mode === "plan"} disabled={working} onClick={() => { setMode("plan"); setStatus(""); }} type="button">{t("planuj")}</button>
                </div>
                <p>{quota ? t("Pozostało: {count} dzisiaj", { count: String(mode === "code" ? quota.codeRemaining : quota.planRemaining) }) : t("Sprawdzanie limitu…")}</p>
                <label htmlFor="ai-prompt">{t(mode === "code" ? "Opisz, co ma zrobić duszek" : "Opisz, jak ma działać gra")}</label>
                <textarea id="ai-prompt" maxLength={4000} onChange={event => { if (!working) setPrompt(event.target.value); }} readOnly={working} value={prompt} />
                <button disabled={working || (mode === "code" && !targetName) || !prompt.trim() || (quota !== undefined && (mode === "code" ? quota.codeRemaining : quota.planRemaining) <= 0)} onClick={() => void submit()} type="button">
                    {working ? t("Asystent pracuje... ({seconds} s.)", { seconds: String(elapsedSeconds) }) :
                        t(mode === "code" ? "Dodaj bloczki do duszka" : "Pokaż plan gry")}
                </button>
                {working && stoppable && <button className="secondary-button" onClick={stop} type="button">{t("Zatrzymaj")}</button>}
                {status && <p className="ai-status" role="status">{status}</p>}
                {mode === "plan" && plan && <button className="ai-plan-proposal-button" onClick={() => setPlanOpen(true)} type="button">{t("Pokaż propozycję bloczków")}</button>}
            </>}
            {planOpen && createPortal(
                <div className="modal-overlay" onMouseDown={event => { if (event.target === event.currentTarget) setPlanOpen(false); }} role="presentation">
                    <section aria-labelledby="ai-plan-title" aria-modal="true" className="import-dialog ai-plan-dialog" role="dialog">
                        <div className="import-dialog-content ai-plan-content">
                            <h2 id="ai-plan-title">{t("Propozycja bloczków")}</h2>
                            {plan && planImage && planLink && <>
                                <div className="ai-plan-actions">
                                    <a href={planLink} rel="noopener noreferrer" target="_blank">{t("Otwórz w nowej karcie")}</a>
                                    <a download="skrypty-scratch.svg" href={planLink}>{t("Pobierz")}</a>
                                </div>
                                <div className="ai-plan-preview-scroll">
                                    <img alt={t("Skrypty gry")} className="ai-block-preview" src={planImage} />
                                </div>
                            </>}
                            <button className="secondary-button" onClick={() => setPlanOpen(false)} ref={closePlanButton} type="button">{t("Zamknij")}</button>
                        </div>
                    </section>
                </div>, document.body,
            )}
        </section>
    );
};
